<script setup>
  import useVuelidate from '@vuelidate/core'

  import { computed } from 'vue'

  import FieldRendererLabel from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererLabel.vue'
  import FieldRendererValidationMessages from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererValidationMessages.vue'
  import FieldRendererWrapper from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererWrapper.vue'
  import { useFieldPlugins } from '@/features/settingsManagement/fieldManagement/edit/plugin/fieldsPlugin.js'
  import {
    fieldCommonProps,
    normalizeFieldExtraData,
  } from '@/features/settingsManagement/fieldManagement/edit/utils/fieldUtils.js'
  import { mergeDeep } from '@/features/utils/object.utils.js'

  defineEmits(['update:modelValue'])

  const props = defineProps({
    ...fieldCommonProps,
  })

  const { getPlugin } = useFieldPlugins()
  const plugin = getPlugin('textarea')

  const extraData = computed(() => {
    return mergeDeep(plugin.defaultExtraData, normalizeFieldExtraData(props.field.extraData))
  })

  const inputMode = computed(() => {
    return extraData.value.inputMode || 'text'
  })

  const placeholder = computed(() => {
    return extraData.value.placeholder || ''
  })

  const rules = computed(() => ({
    value: plugin.getValidations({ props }),
  }))

  const state = computed(() => ({
    value: props.modelValue,
  }))

  const v$ = useVuelidate(rules, state)

  async function validate() {
    const result = await v$.value.$validate()
    return {
      isValid: result,
      errors: Object.entries(v$.value.$errors).map(([, error]) => error.$message),
    }
  }

  defineExpose({
    validate,
  })
</script>

<template>
  <FieldRendererWrapper :field="field">
    <template #label>
      <FieldRendererLabel :field="field" :external-required="externalRequired" />
    </template>

    <Textarea
      :inputmode="inputMode"
      :placeholder="placeholder"
      :value="modelValue"
      :rows="3"
      :disabled="disabled"
      fluid
      :invalid="v$.value.$error || hasExternalError"
      @input="$emit('update:modelValue', $event.target.value)"
    />

    <template #validation>
      <FieldRendererValidationMessages v-if="v$.value.$error" :error-messages="v$.value.$errors" />
    </template>
  </FieldRendererWrapper>
</template>
